/**
 * Typed parsing of the CedarReachAI error envelope (design doc §13.1).
 *
 * ```json
 * { "error": { "code": "APPROVAL_INVALIDATED", "message": "...",
 *              "request_id": "req_...", "details": { ... } } }
 * ```
 *
 * The envelope is turned into a discriminated union so UI code can `switch`
 * on `failure.code` and get the right `details` shape, instead of string
 * matching on messages.
 */
import { z } from 'zod';

/**
 * Stable machine-readable codes (CLAUDE.md §3.4).
 *
 * GENERATED from `contracts/openapi.yaml` by
 * `scripts/gen_client_error_codes.py`. Do not edit by hand — the Rust
 * crates are the source of truth, the OpenAPI document restates them, and
 * this list is derived so it cannot drift from either.
 */
export const API_ERROR_CODES = [
  'INVALID_ID',
  'INVALID_HASH',
  'INVALID_LOCALE',
  'INVALID_SLUG',
  'INVALID_CURRENCY',
  'INVALID_REGION',
  'INVALID_URL',
  'INVALID_EXTERNAL_REF',
  'INVALID_AMOUNT',
  'INVALID_TIME_WINDOW',
  'VERSION_OVERFLOW',
  'UNKNOWN_AUTOMATION_LEVEL',
  'INVALID_VALUE',
  'SCORE_OUT_OF_RANGE',
  'ILLEGAL_CONTENT_TRANSITION',
  'ACTOR_NOT_PERMITTED',
  'APPROVAL_INVALIDATED',
  'INSUFFICIENT_EVIDENCE',
  'UNSUPPORTED_CLAIM',
  'AUTOMATION_LEVEL_TOO_LOW',
  'FORBIDDEN',
  'CURRENCY_MISMATCH',
  'BUDGET_EXCEEDED',
  'MISSING_EVIDENCE',
  'ARITHMETIC_OVERFLOW',
  'POLICY_BLOCKED',
  'MODEL_OUTPUT_INVALID',
  'TOOL_NOT_ALLOWED',
  'DOMAIN_NOT_ALLOWED',
  'RUN_CANCELLED',
  'PROVIDER_UNAVAILABLE',
  'PROMPT_TEMPLATE_INVALID',
  'UNKNOWN_SCHEMA',
  'NOT_FOUND',
  'VERSION_CONFLICT',
  'CONFLICT',
  'APPEND_ONLY_VIOLATION',
  'INTERNAL',
  'UNAUTHENTICATED',
  'REAUTHENTICATION_REQUIRED',
  'CSRF_REJECTED',
  'WORKSPACE_PAUSED',
  'IDEMPOTENCY_KEY_REUSED',
  'IDEMPOTENCY_REQUEST_IN_PROGRESS',
  'VALIDATION_FAILED',
  'CONNECTOR_REAUTH_REQUIRED',
  'RATE_LIMITED',
  'DATABASE_BUSY',
  'PUBLICATION_STATE_UNKNOWN',
  'RUN_REAPED',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    request_id: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

interface FailureBase {
  readonly kind: 'api';
  readonly status: number;
  readonly message: string;
  readonly requestId: string | undefined;
  /**
   * `true` when the same request may be replayed unchanged once the transient
   * condition clears. Never `true` for a state the user must resolve first.
   */
  readonly retriable: boolean;
}

/** The approved content or destination changed underneath an approval. */
export interface ApprovalInvalidatedFailure extends FailureBase {
  readonly code: 'APPROVAL_INVALIDATED';
  readonly details: {
    readonly approval_request_id?: string;
    readonly changed_fields?: readonly string[];
  };
}

/** A public claim had no `verified` fact behind it — blocking, never a warning. */
export interface UnsupportedClaimFailure extends FailureBase {
  readonly code: 'UNSUPPORTED_CLAIM';
  readonly details: {
    readonly content_version_id?: string;
    readonly claims?: readonly { readonly claim_id?: string; readonly text?: string }[];
  };
}

export interface VersionConflictFailure extends FailureBase {
  readonly code: 'VERSION_CONFLICT';
  readonly details: {
    readonly expected_version?: number;
    readonly actual_version?: number;
    readonly resource?: string;
  };
}

export interface IdempotencyKeyReusedFailure extends FailureBase {
  readonly code: 'IDEMPOTENCY_KEY_REUSED';
  readonly details: { readonly idempotency_key?: string };
}

export interface WorkspacePausedFailure extends FailureBase {
  readonly code: 'WORKSPACE_PAUSED';
  readonly details: { readonly workspace_id?: string; readonly paused_at?: string };
}

export interface BudgetExceededFailure extends FailureBase {
  readonly code: 'BUDGET_EXCEEDED';
  readonly details: {
    readonly limit?: string;
    readonly currency?: string;
    readonly committed?: string;
  };
}

export interface ValidationFailure extends FailureBase {
  readonly code: 'VALIDATION_FAILED';
  /** `ValidationFailedDetails` in the contract, where a route sends it. */
  readonly details: {
    readonly fields?: readonly { readonly field: string; readonly reason: string }[];
  };
}

export interface RateLimitedFailure extends FailureBase {
  readonly code: 'RATE_LIMITED';
  readonly details: { readonly retry_after_seconds?: number };
}

/**
 * The publisher could not determine whether the external mutation landed.
 * The UI must block manual retry until the reconciler has run (§22.4).
 */
export interface PublicationStateUnknownFailure extends FailureBase {
  readonly code: 'PUBLICATION_STATE_UNKNOWN';
  readonly details: { readonly publication_id?: string };
}

/** Any other envelope code, including codes added by the server after this build. */
export interface GenericApiFailure extends FailureBase {
  readonly code: Exclude<
    ApiErrorCode,
    | 'APPROVAL_INVALIDATED'
    | 'UNSUPPORTED_CLAIM'
    | 'VERSION_CONFLICT'
    | 'IDEMPOTENCY_KEY_REUSED'
    | 'WORKSPACE_PAUSED'
    | 'BUDGET_EXCEEDED'
    | 'VALIDATION_FAILED'
    | 'RATE_LIMITED'
    | 'PUBLICATION_STATE_UNKNOWN'
  >;
  readonly details: Readonly<Record<string, unknown>>;
}

/** Request never produced an HTTP response (offline, DNS, abort). Always retriable. */
export interface TransportFailure {
  readonly kind: 'transport';
  readonly message: string;
  readonly requestId: string | undefined;
  readonly cause: unknown;
  readonly retriable: true;
}

/** An HTTP error response whose body was not a valid error envelope. */
export interface MalformedFailure {
  readonly kind: 'malformed';
  readonly status: number;
  readonly message: string;
  readonly requestId: string | undefined;
  readonly body: string;
  readonly retriable: boolean;
}

export type ApiFailure =
  | ApprovalInvalidatedFailure
  | UnsupportedClaimFailure
  | VersionConflictFailure
  | IdempotencyKeyReusedFailure
  | WorkspacePausedFailure
  | BudgetExceededFailure
  | ValidationFailure
  | RateLimitedFailure
  | PublicationStateUnknownFailure
  | GenericApiFailure
  | TransportFailure
  | MalformedFailure;

/** HTTP statuses whose failure is purely transient. */
const RETRIABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

/**
 * Codes that are *never* auto-retriable regardless of status, because replaying
 * the request cannot succeed until a human resolves the underlying state.
 */
const NON_RETRIABLE_CODES: ReadonlySet<string> = new Set<string>([
  'APPROVAL_INVALIDATED',
  'UNSUPPORTED_CLAIM',
  'INSUFFICIENT_EVIDENCE',
  'WORKSPACE_PAUSED',
  'AUTOMATION_LEVEL_TOO_LOW',
  'BUDGET_EXCEEDED',
  'FORBIDDEN',
  'UNAUTHENTICATED',
  'NOT_FOUND',
  'VERSION_CONFLICT',
  'IDEMPOTENCY_KEY_REUSED',
  'VALIDATION_FAILED',
  'CONNECTOR_REAUTH_REQUIRED',
  // Explicitly non-retriable: the operator must wait for reconciliation (§22.4).
  'PUBLICATION_STATE_UNKNOWN',
]);

/**
 * Codes that are retriable whatever their status: the identical request
 * succeeds once the transient condition passes. A `409
 * IDEMPOTENCY_REQUEST_IN_PROGRESS` means the first request with the same
 * `Idempotency-Key` is still running; retrying with the **same key** is
 * answered with that request's response.
 */
const RETRIABLE_CODES: ReadonlySet<string> = new Set<string>([
  'RATE_LIMITED',
  'IDEMPOTENCY_REQUEST_IN_PROGRESS',
]);

function isKnownCode(code: string): code is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(code);
}

/** Parse an error response body into the typed union. Never throws. */
export function parseErrorEnvelope(
  status: number,
  body: unknown,
  fallbackRequestId?: string | undefined,
): ApiFailure {
  const parsed = errorEnvelopeSchema.safeParse(body);
  if (!parsed.success) {
    return {
      kind: 'malformed',
      status,
      message: `Request failed with status ${status}.`,
      requestId: fallbackRequestId,
      body: typeof body === 'string' ? body : JSON.stringify(body ?? null),
      retriable: RETRIABLE_STATUS.has(status),
    };
  }

  const { code, message, request_id, details } = parsed.data.error;
  const normalizedCode: ApiErrorCode = isKnownCode(code) ? code : 'INTERNAL';
  const retriable = NON_RETRIABLE_CODES.has(normalizedCode)
    ? false
    : RETRIABLE_STATUS.has(status) || RETRIABLE_CODES.has(normalizedCode);

  return {
    kind: 'api',
    code: normalizedCode,
    status,
    message,
    requestId: request_id ?? fallbackRequestId,
    details: details ?? {},
    retriable,
  } as ApiFailure;
}

export function isApiFailure(value: unknown): value is ApiFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value.kind === 'api' || value.kind === 'transport' || value.kind === 'malformed')
  );
}

export function isRetriable(failure: ApiFailure): boolean {
  return failure.retriable;
}

/** A thrown wrapper so failures survive `throw`/`catch` boundaries (TanStack Query). */
export class ApiClientError extends Error {
  readonly failure: ApiFailure;

  constructor(failure: ApiFailure) {
    super(failure.message);
    this.name = 'ApiClientError';
    this.failure = failure;
  }
}

export function toFailure(error: unknown): ApiFailure | undefined {
  if (error instanceof ApiClientError) return error.failure;
  if (isApiFailure(error)) return error;
  return undefined;
}

/**
 * How long to wait before retrying a `429 RATE_LIMITED` or a `503
 * DATABASE_BUSY` (ADR-070: nothing took effect; retry with the same
 * `Idempotency-Key`), in milliseconds, from the envelope's
 * `details.retry_after_seconds`; `null` for any other failure or when the
 * server named no wait.
 *
 * Invariant: never shorter than the server asked. The contract's
 * `retry_after_seconds` is whole seconds, but a fractional value (or one a
 * proxy rewrote) is rounded **up** to the next second, so the retry never
 * arrives before the server would accept it (ADR-049).
 */
export function retryAfterMs(failure: ApiFailure | undefined): number | null {
  if (!failure || failure.kind !== 'api') return null;
  if (failure.code !== 'RATE_LIMITED' && failure.code !== 'DATABASE_BUSY') return null;
  const seconds = failure.details.retry_after_seconds;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.ceil(seconds) * 1000;
}
